import { Location } from '@angular/common';
import { Component, HostListener, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { ClientStateEnums } from 'src/app/enum/client-state-enums';
import { ConstructionSiteStateEnums } from 'src/app/enum/construction-site-enums';
import { DevolutionProjectionModel } from 'src/app/models/preliminaries/devolution-projection';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { DateConverterService } from 'src/app/services/global-services/date-converter.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { InvoiceService } from 'src/app/services/invoicing/invoice.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { DevolutionProjectionService } from 'src/app/services/preliminaries/devolution-projection.service';
import { ServiceService } from 'src/app/services/rental/service.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-devolution-projection',
  templateUrl: './register-devolution-projection.component.html',
  styleUrls: ['./register-devolution-projection.component.scss'],
  standalone: false
})
export class RegisterDevolutionProjectionComponent implements OnInit {

  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly branchService = inject(BranchService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly dateConverterService = inject(DateConverterService);
  private readonly devolutionProjectionService = inject(DevolutionProjectionService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly headerService = inject(HeaderService);
  private readonly invoiceService = inject(InvoiceService);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly serviceService = inject(ServiceService);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly windowService = inject(WindowService);

  branchId: number = 0;
  clientInvalid: boolean = false;
  costCenterInvalid: boolean = false;
  devolutionProjectionContract: DevolutionProjectionModel = new DevolutionProjectionModel();
  devolutionProjectionId: number = 0;
  filteredCostCenters: any[] = [];
  formDevolutionProjection: FormGroup = new FormGroup({});
  isOpened: string | undefined = undefined;
  listBranch: any[] = [];
  listClients: any[] = [];
  listCostCenter: any[] = [];
  nameWindow: string = 'Captura Proyección Devolución';
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  quantityDetails: number = 0;
  url: string = 'devolution-projection';

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.validateForm(new DevolutionProjectionModel())
    this.activatedRoute.params.subscribe(params => {
      this.devolutionProjectionId = Object.keys(params).length > 0 ? params?.id : null;
    });
    await this.getPermission();
    await this.handleInitialState();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async changeBranch(branchId: number, loadAllLists: boolean) {
    this.branchId = branchId;
    if (loadAllLists) {
      await this.getAllLists();
    } else {
      await this.getAllLists('costCenters');
      this.resetContract();
    }
  }

  async changeClient(clientId: number | null) {
    const previousId = this.devolutionProjectionContract.costCenter?.clientId;
    if (this.quantityDetails > 0 && clientId !== previousId) {
      const msg = !clientId
        ? 'No puede limpiar el cliente si la proyección tiene detalle.'
        : 'No puede cambiar el cliente si la proyección tiene detalle.';
      this.toastHandlerService.showToast('error', msg);
      setTimeout(() => this.formDevolutionProjection.get("clientId")?.patchValue(previousId));
      return;
    }
    if (!clientId || clientId !== previousId) {
      this.formDevolutionProjection.get("costCenterId")?.patchValue(null);
    }
    if (!clientId) {
      this.filteredCostCenters = this.listCostCenter;
      return;
    }

    this.filteredCostCenters = this.listCostCenter.filter(x => x.clientId === clientId);
    await this.validateClientState(clientId);
  }

  async changeCostCenter(costCenterId: number | null) {
    if (!costCenterId) {
      this.resetContract();
      this.filteredCostCenters = [...this.listCostCenter];
      return;
    }
    await this.getDevolutionProjectionByKeys(costCenterId);
  }

  async createDevolutionProjection(model: DevolutionProjectionModel) {
    try {
      const response = await firstValueFrom(this.devolutionProjectionService.createDevolutionProjection(model));
      this.devolutionProjectionId = response.devolutionProjectionId;
      this.devolutionProjectionContract = response;
      this.validateForm(this.devolutionProjectionContract);
      this.location.replaceState(`/${this.url}/register/${this.devolutionProjectionId}`);
      this.toastHandlerService.showToast(
        'success',
        'La proyección fue creada exitosamente.'
      );
    } catch (error) {
      console.error('Error al crear la proyección.', error);
    }
  }

  async createNewRegister() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para crear la proyección de devolución. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    this.resetContract();
  }

  async deleteDevolutionProjection() {
    if (!this.devolutionProjectionId) {
      this.toastHandlerService.showToast(
        'warning',
        'Debe crear una proyección de devolución.'
      );
      return;
    }
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        'El usuario no tiene permisos para eliminar la proyección de devolución. Comuníquese con el administrador del sistema.'
      );
      return;
    }
    const accepted = await this.confirmDialogService.confirm('¿Realmente desea eliminar la proyección de devolución actual?');
    if (accepted) {
      try {
        await firstValueFrom(this.devolutionProjectionService.deleteDevolutionProjection(this.devolutionProjectionId));
        this.toastHandlerService.showToast(
          'success',
          'La proyección de devolución fue eliminada exitosamente.'
        );
        this.resetContract();
      } catch (error) {
        console.error("Error al eliminar la proyección de devolución.", error);
      }
    }
  }

  async getAllLists(...listsToLoad: Array<'branches' | 'costCenters' | 'clients'>) {
    try {
      const lists = {
        branches: async () => {
          const response = await firstValueFrom(this.branchService.getAllBranchesForSelectAsync());
          this.listBranch = response;
        },
        costCenters: async () => {
          const response = await firstValueFrom(this.devolutionProjectionService.getCostCenterByBranchForSelect(this.branchId));
          this.filteredCostCenters = this.listCostCenter = response;
        },
        clients: async () => {
          const response = await firstValueFrom(this.devolutionProjectionService.getClientsForSelectAsync());
          this.listClients = response;
        }
      };
      if (listsToLoad.length) {
        await Promise.all(listsToLoad.map(list => lists[list]()));
      } else {
        await Promise.all([
          lists.branches(),
          lists.costCenters(),
          lists.clients()
        ]);
      }
    } catch (error) {
      console.error('Error obtener las listas.', error);
    }
  }

  async getButtonClick(buttonClick: any) {
    const branchId = this.formDevolutionProjection.get("branchId")?.value;
    if (!branchId) {
      this.toastHandlerService.showToast(
        'warning',
        'Seleccione una sucursal.'
      );
      return;
    }
    await this.handleMasterDevolutionProjectionNavigation(buttonClick, branchId)
  }

  getCostCenter(costCenterId: number | null) {
    const controls = this.formDevolutionProjection.controls;
    if (!costCenterId) {
      const clientId = controls.clientId?.value;
      this.filteredCostCenters = this.listCostCenter.filter(x => !clientId || x.clientId === clientId);
      this.clientInvalid = this.costCenterInvalid = false;
      return;
    }
    const costCenter = this.listCostCenter.find(x => x.value === costCenterId);
    const clientId = costCenter?.clientId;
    const client = this.listClients.find(x => x.value === clientId);
    this.clientInvalid = client?.clientStateId === ClientStateEnums.inactivo;
    this.costCenterInvalid = costCenter?.constructionSiteStateId === ConstructionSiteStateEnums.inactiva;
    this.filteredCostCenters = this.listCostCenter.filter(x => x.clientId === clientId);
    setTimeout(() => {
      controls.costCenterId.patchValue(costCenterId);
      controls.clientId.patchValue(costCenter?.clientId);
    });
  }

  async getDevolutionProjectionById() {
    try {
      const response = await firstValueFrom(this.devolutionProjectionService.getDevolutionProjectionById(this.devolutionProjectionId));
      await this.changeBranch(response.branchId, true);
      this.devolutionProjectionContract = response;
      this.validateForm(this.devolutionProjectionContract);
    } catch (error) {
      console.error('Error al obtener la solución por id.', error);
    }
  }

  async getDevolutionProjectionByKeys(costCenterId: number) {
    try {
      const response = await firstValueFrom(
        this.devolutionProjectionService.getDevolutionProjectionByKeys(this.branchId, costCenterId)
      );
      await this.validateCostCenterState(costCenterId);
      if (response && response.devolutionProjectionId) {
        this.devolutionProjectionId = response.devolutionProjectionId;
        this.location.replaceState(`/${this.url}/register/${this.devolutionProjectionId}`);
      } else {
        this.devolutionProjectionId = 0;
        this.location.replaceState(`/${this.url}/register`);
      }
      this.devolutionProjectionContract = response;
      this.validateForm(this.devolutionProjectionContract);
    } catch (error) {
      console.error('Error al obtener la solución por claves.', error);
    }
  }

  async getFirstBranch() {
    try {
      const response = await firstValueFrom(this.branchService.getFirstBranchByUserAuthenticatedAsync());
      await this.changeBranch(response.branchId, true);
    } catch (error) {
      console.error('Error al obtener la sucursal del usuario.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await this.getPermissionProfileByUrl();
  }

  async getPermissionProfileByUrl() {
    try {
      const response = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
      this.permissionUpdatable = response;
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  goToBack(event: Event) {
    event.preventDefault();
    this.router.navigate([`/${this.url}`]);
  }

  async handleInitialState() {
    if (this.devolutionProjectionId) {
      await this.getDevolutionProjectionById();
    } else {
      await this.getFirstBranch();
      this.createNewRegister();
    }
  }

  async handleMasterDevolutionProjectionNavigation(buttonClick: any, branchId: number) {
    const actionMap: Record<string, () => Promise<DevolutionProjectionModel>> = {
      first: () => firstValueFrom(
        this.devolutionProjectionService.getFirstDevolutionProjectionByKeys(branchId)
      ),
      previus: () => firstValueFrom(
        this.devolutionProjectionService.getPreviusDevolutionProjectionByKeys(branchId, this.devolutionProjectionId)
      ),
      next: () => firstValueFrom(
        this.devolutionProjectionService.getNextDevolutionProjectionByKeys(branchId, this.devolutionProjectionId)
      ),
      last: () => firstValueFrom(
        this.devolutionProjectionService.getLastDevolutionProjectionByKeys(branchId)
      ),
    };
    const action = actionMap[buttonClick.target.id];
    if (!action) {
      this.resetContract();
      return;
    }
    try {
      const devolutionProjection = await action();
      this.devolutionProjectionId = devolutionProjection.devolutionProjectionId;
      this.devolutionProjectionContract = devolutionProjection;
      this.validateForm(this.devolutionProjectionContract);
      this.location.replaceState(`/${this.url}/register/${this.devolutionProjectionId}`);
    } catch (error) {
      console.error(`Error al buscar la proyección (${buttonClick.target.id}).`, error);
    }
  }

  async resetContract() {
    this.devolutionProjectionId = 0;
    this.devolutionProjectionContract = new DevolutionProjectionModel();
    this.devolutionProjectionContract.branchId = this.branchId;
    this.validateForm(this.devolutionProjectionContract);
    this.location.replaceState(`/${this.url}/register`);
  }

  async send() {
    this.validateFormControls();
    if (!this.formDevolutionProjection.valid) {
      this.showValidationMessages();
      return;
    }
    const model = this.formDevolutionProjection.getRawValue() as DevolutionProjectionModel;
    if (this.devolutionProjectionId == 0) {
      await this.createDevolutionProjection(model);
    } else {
      await this.updateDevolutionProjection(model);
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      branchId: {
        required: 'El campo Sucursal es obligatorio.',
        min: 'Debes seleccionar una Sucursal válida.'
      },
      costCenterId: {
        required: 'El campo Centro de Costo es obligatorio.',
        min: 'Debes seleccionar un Centro de Costo válido.'
      },
      dateDevolutionProjection: {
        required: 'El campo Fecha de Proyección de Devolución es obligatorio.'
      }
    };

    Object.keys(controlMessages).forEach(control => {
      const formControl = this.formDevolutionProjection.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  async updateDevolutionProjection(model: DevolutionProjectionModel) {
    try {
      this.devolutionProjectionContract.dateDevolutionProjection = model.dateDevolutionProjection;
      const response = await firstValueFrom(this.devolutionProjectionService.updateDevolutionProjection(this.devolutionProjectionContract));
      this.devolutionProjectionContract = response;
      this.validateForm(this.devolutionProjectionContract);
      this.toastHandlerService.showToast(
        'success',
        'La proyección fue modificada exitosamente.'
      );
    } catch (error) {
      console.error('Error al actualizar la proyección.', error);
    }
  }

  async validateClientState(clientId: number) {
    try {
      const client = await firstValueFrom(this.invoiceService.getClientByIdForRegisterInvoiceAsync(clientId));
      this.clientInvalid = client.clientStateId === ClientStateEnums.inactivo;
      if (this.clientInvalid) {
        this.toastHandlerService.showToast(
          'warning',
          'El cliente seleccionado se encuentra inactivo.'
        );
      }
    } catch (error) {
      console.error('Error al validar el estado del cliente.', error);
      this.clientInvalid = false;
    }
  }

  async validateCostCenterState(costCenterId: number) {
    try {
      const clientIdControl = this.formDevolutionProjection.get('clientId');
      const response = await firstValueFrom(this.serviceService.getCostCenterByIdAsync(costCenterId));
      if (clientIdControl?.value !== response.clientId) {
        clientIdControl?.patchValue(response.clientId);
      }
      this.filteredCostCenters = this.listCostCenter.filter(x => x.clientId === response.clientId);
      this.costCenterInvalid = response.constructionSiteStateId === ConstructionSiteStateEnums.inactiva;
      if (this.costCenterInvalid) {
        this.toastHandlerService.showToast(
          'warning',
          'El centro de costos seleccionado se encuentra inactivo.'
        );
      }
    } catch (error) {
      console.error('Error al obtener el estado del centro de costos.', error);
    }
  }

  validateForm(devolutionProjection: DevolutionProjectionModel) {
    this.formDevolutionProjection = this.formBuilder.group({
      clientId: [],
      branchId: [devolutionProjection.branchId, [Validators.required, Validators.min(1)]],
      costCenterId: [devolutionProjection.costCenterId, [Validators.required, Validators.min(1)]],
      dateDevolutionProjection: [devolutionProjection.dateDevolutionProjection ?
        this.dateConverterService.convertUtcToLocalDate(devolutionProjection.dateDevolutionProjection) : null, [Validators.required]],
    });
    this.getCostCenter(devolutionProjection.costCenterId);
  }

  validateFormControls() {
    Object.keys(this.formDevolutionProjection.controls).forEach((key) => {
      const control = this.formDevolutionProjection.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
